import { useQueryOriginStatus } from '@monorepo/shared/hooks/useQueryOriginStatus';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { useSkipWidgetLink } from '@monorepo/shared/componentsV2/SkipWidgetLink';
import {
  IconSlotCell,
  IconSlotColumnHeader,
} from '@monorepo/shared/componentsV2/Table/IconSlotCell';
import {
  Pagination,
  PaginationOnPageChange,
} from '@monorepo/shared/componentsV2/Table/Pagination';
import { Table } from '@monorepo/shared/componentsV2/Table/Table';
import {
  ColumnLayout,
  TableColumn,
} from '@monorepo/shared/componentsV2/Table/types';
import { Tooltip } from '@monorepo/shared/componentsV2/Tooltip';
import { helpDeskButtonDiameter } from '@monorepo/shared/constants/helpDesk';
import {
  fillHeightAndScrollable,
  verticalLayout,
} from '@monorepo/shared/styles/layout';

import { TruncatedLabel } from '@monorepo/shared/componentsV2/TruncatedLabel';
import { ColumnType, QueryResponse } from 'mapistry-shared';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import {
  DisplayQueryColumn,
  DisplayResultRow,
  getDisplayQueryColumns,
  mapResultRows,
  SpecialColumnType,
} from './resultsMapper';
import { QueryOriginStatusAlert } from './QueryOriginStatusAlert';
import { QueryResultsTableCellFactory } from './QueryResultsTableCellFactory';

const Container = styled.div`
  ${verticalLayout}
`;

const StyledLoading = styled(Loading)`
  padding: 5rem;
`;

const StyledTable = styled(Table)`
  ${fillHeightAndScrollable}
` as typeof Table;

const StyledTruncatedLabel = styled(TruncatedLabel)<{
  $align?: ColumnLayout['align'];
}>`
  float: ${({ $align }) => ($align === 'right' ? 'right' : undefined)};
  width: fit-content;
  max-width: 28vw;
`;

const IncompleteDataTooltip = styled(Tooltip)`
  && svg {
    width: 1rem;
    height: 1rem;
  }
`;

const Footer = styled.div`
  flex-shrink: 0;
  min-height: ${helpDeskButtonDiameter};
`;

interface QueryResultsTableProps {
  className?: string;
  isLoading: boolean;
  originId: string;
  organizationId: string;
  projectId: string;
  onPageChange?: PaginationOnPageChange;
  queryResult?: QueryResponse;
  ariaName?: string;
}

export const INCOMPLETE_DATA_TOOLTIP =
  'There are one or more values enabled for this field. A single calculation cannot be made.';

function ColumnHeader({
  align,
  isLimitColumn,
  queryColumn,
}: {
  align: ColumnLayout['align'];
  isLimitColumn: boolean;
  queryColumn: DisplayQueryColumn;
}) {
  const label = (
    <StyledTruncatedLabel $align={align} label={queryColumn.columnLabel} />
  );
  if (!isLimitColumn && !queryColumn.isIncompleteData) return label;
  return (
    <IconSlotColumnHeader
      icon={
        queryColumn.isIncompleteData ? (
          <IncompleteDataTooltip title={INCOMPLETE_DATA_TOOLTIP} />
        ) : undefined
      }
    >
      {label}
    </IconSlotColumnHeader>
  );
}

function getColumnLayout(queryColumn: DisplayQueryColumn): ColumnLayout {
  switch (queryColumn.columnType) {
    case ColumnType.NUMBER:
    case SpecialColumnType.LIMIT_COLUMN:
    case ColumnType.BOOLEAN:
    case ColumnType.DATE:
    case ColumnType.DATETIME:
    case ColumnType.DATE_RANGE:
    case ColumnType.TEXT:
    case ColumnType.TIME:
    case ColumnType.FOREIGN_ID:
      return {};
    default: {
      const exhaustiveCheck: never = queryColumn.columnType;
      throw new Error(`Invalid column type - ${exhaustiveCheck}`);
    }
  }
}

export function QueryResultsTable({
  className,
  isLoading,
  onPageChange,
  queryResult,
  ariaName = 'Current Query',
  originId,
  organizationId,
  projectId,
}: QueryResultsTableProps) {
  const {
    columns: queryColumns = [],
    columnSets = [],
    limitColumnSets = [],
    results: {
      pagination = {
        fullCount: 0,
        currentPage: 0,
        perPage: 50,
        from: 0,
        to: 0,
      },
    } = {},
  } = queryResult || {};

  const { originStatus } = useQueryOriginStatus({
    organizationId,
    projectId,
    originId,
  });

  const rows: DisplayResultRow[] = useMemo(
    () => (queryResult ? mapResultRows(queryResult) : []),
    [queryResult],
  );

  const columns: TableColumn<DisplayResultRow>[] = useMemo(
    () =>
      getDisplayQueryColumns(queryColumns, limitColumnSets, columnSets)
        .filter((c) => !c.isHidden)
        .map((queryColumn: DisplayQueryColumn) => {
          const { columnName } = queryColumn;
          const columnLayout = getColumnLayout(queryColumn);
          const isLimitColumn =
            queryColumn.columnType === SpecialColumnType.LIMIT_COLUMN;
          return {
            id: columnName,
            header: (
              <ColumnHeader
                align={columnLayout.align}
                isLimitColumn={isLimitColumn}
                queryColumn={queryColumn}
              />
            ),
            ...columnLayout,
            // eslint-disable-next-line react/no-unstable-nested-components
            contents: (row: DisplayResultRow) => {
              const cell = (
                <QueryResultsTableCellFactory
                  key={row.id}
                  column={queryColumn}
                  columns={queryColumns}
                  columnSets={columnSets}
                  row={row}
                />
              );
              if (isLimitColumn || !queryColumn.isIncompleteData) return cell;
              return (
                <IconSlotCell key={row.id} icon={null} isIconVisible={false}>
                  {cell}
                </IconSlotCell>
              );
            },
          };
        }),
    [columnSets, limitColumnSets, queryColumns],
  );

  const { SkipLink, SkipLinkTarget } = useSkipWidgetLink();

  return (
    <Container className={className}>
      <QueryOriginStatusAlert
        originStatus={originStatus}
        areQueryResultsLoading={isLoading}
      />
      {isLoading ? (
        <StyledLoading />
      ) : (
        <>
          <SkipLink linkText="Skip table content" />
          <StyledTable
            ariaLabel={`${ariaName} Results`}
            columns={columns}
            makeTableFocusable
            rows={rows}
          />
          <SkipLinkTarget />
          {/* if onPageChange wasn't passed in we assume you don't want pagination */}
          {onPageChange ? (
            <Pagination
              onPageChange={onPageChange}
              page={pagination.currentPage || 0}
              rowsPerPage={pagination.perPage}
              totalRowCount={pagination.fullCount ?? 0}
            />
          ) : (
            <Footer />
          )}
        </>
      )}
    </Container>
  );
}
